import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { assessStep, type ProcedureWithSteps, type CycleFull, type Workstation, type StepFull } from '@visionqc/shared';

/**
 * Operator / assessment screen — modelled on Jidoka's Nagare screens: a live
 * labelled camera view (part/tool boxes + per-ROI counts + overlay text) and the
 * activity table (Standard / Acceptable / Time-taken / Range / Standard count /
 * Count by user / Evaluation / Remark) that scores each step as the operator works.
 * A staff header tags the run for skill assessment. Start / Stop watch the screen;
 * Reset abandons the current cycle; a blocked critical step needs an override.
 *
 * Scoring is the shared `assessStep` — one source of truth for screen and report.
 */

type Det = { cls: string; x: number; y: number; w: number; h: number; confidence: number };

const RANGE_TONE: Record<string, string> = { excellent: 'var(--good)', acceptable: 'var(--accent)', not_acceptable: 'var(--bad)' };
const RANGE_LABEL: Record<string, string> = { excellent: 'Excellent', acceptable: 'Acceptable', not_acceptable: 'Not Acceptable' };

function pointInPoly(px: number, py: number, poly: Array<{ x: number; y: number }>): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i]!, b = poly[j]!;
    if (a.y > py !== b.y > py && px < ((b.x - a.x) * (py - a.y)) / (b.y - a.y) + a.x) inside = !inside;
  }
  return inside;
}
const centre = (d: Det) => ({ x: d.x + d.w / 2, y: d.y + d.h / 2 });

export function ProcGuardKioskView({
  apiBase = '/api/procguard',
  snapshotBase = '/api/safety',
  workstationId: wsProp,
  onExit,
}: {
  apiBase?: string;
  snapshotBase?: string;
  workstationId?: number;
  onExit?: () => void;
} = {}) {
  const params = useParams<{ id: string }>();
  const workstationId = wsProp ?? Number(params.id);
  const nav = useNavigate();
  const rootRef = useRef<HTMLDivElement>(null);
  const A = apiBase;

  const [ws, setWs] = useState<Workstation | null>(null);
  const [procedure, setProcedure] = useState<ProcedureWithSteps | null>(null);
  const [cycle, setCycle] = useState<CycleFull | null>(null);
  const [dets, setDets] = useState<Det[]>([]);
  const [running, setRunning] = useState(true);
  const [tick, setTick] = useState(0);

  const [staffName, setStaffName] = useState('');
  const [staffId, setStaffId] = useState('');
  const [assessName, setAssessName] = useState('');
  const [assessNo, setAssessNo] = useState('');
  const taggedRef = useRef<number | null>(null);

  const loadLive = useCallback(() => {
    fetch(`${A}/workstations/${workstationId}/live`).then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) { setWs(d.workstation); setProcedure(d.procedure); setCycle(d.cycle); } }).catch(() => undefined);
  }, [A, workstationId]);
  const loadDets = useCallback(() => {
    fetch(`${A}/workstations/${workstationId}/detect`).then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setDets(d.detections)).catch(() => undefined);
  }, [A, workstationId]);

  useEffect(() => {
    loadLive();
    const clock = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(clock);
  }, [loadLive]);
  useEffect(() => {
    if (!running) return;
    loadLive(); loadDets();
    const poll = setInterval(() => { loadLive(); loadDets(); }, 1200);
    return () => clearInterval(poll);
  }, [running, loadLive, loadDets]);

  // Tag a new running cycle with the assessor, once, when staff is set.
  useEffect(() => {
    if (cycle && staffName.trim() && taggedRef.current !== cycle.id) {
      taggedRef.current = cycle.id;
      void fetch(`${A}/cycles/${cycle.id}/assess`, {
        method: 'POST', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ operator: staffName.trim(), assessment: { staffId, assessmentName: assessName, assessmentNumber: assessNo } }),
      }).catch(() => undefined);
    }
  }, [cycle, staffName, staffId, assessName, assessNo]);

  const primaryCam = ws?.cameras.find((c) => c.isPrimary)?.cameraId ?? ws?.cameras[0]?.cameraId ?? null;
  const actualByPart = new Map<string, number>();
  for (const d of dets) actualByPart.set(d.cls.toLowerCase(), (actualByPart.get(d.cls.toLowerCase()) ?? 0) + 1);

  const unresolved = (cycle?.deviations ?? []).filter((d) => !d.resolved);
  const blockedDev = unresolved.find((d) => d.severity === 'critical');
  const blocked = Boolean(blockedDev);
  const cycleElapsed = cycle?.startedAt ? Math.max(0, Math.round((Date.now() - new Date(cycle.startedAt).getTime()) / 1000)) : 0;
  const activeStep = cycle?.steps.find((s) => s.status === 'active');

  const cycleStep = (seq: number) => cycle?.steps.find((s) => s.seq === seq);
  const timeTakenS = (seq: number) => {
    const cs = cycleStep(seq);
    if (!cs) return null;
    if (cs.durationMs != null) return cs.durationMs / 1000;
    if (cs.status === 'active' && cs.startedAt) return Math.round((Date.now() - new Date(cs.startedAt).getTime()) / 1000);
    return null;
  };
  const standardCount = (s: StepFull) => (s.components.length ? s.components.reduce((n, c) => n + c.quantity, 0) : null);
  const actualCount = (s: StepFull) => (s.components.length ? s.components.reduce((n, c) => n + (actualByPart.get(c.partNumber.toLowerCase()) ?? 0), 0) : null);

  async function doOverride() {
    if (!cycle || !blockedDev) return;
    const seq = cycle.steps.find((s) => s.stepId === blockedDev.stepId)?.seq ?? activeStep?.seq;
    if (seq == null) return;
    const reason = prompt('Supervisor override — reason (logged):');
    if (!reason || reason.trim().length < 3) return;
    await fetch(`${A}/cycles/${cycle.id}/override`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ seq, reason: reason.trim() }) }).catch(() => undefined);
    loadLive();
  }
  async function doReset() {
    if (!confirm('Reset — abandon the current cycle?')) return;
    await fetch(`${A}/workstations/${workstationId}/reset`, { method: 'POST' }).catch(() => undefined);
    taggedRef.current = null;
    loadLive();
  }
  const fullscreen = () => {
    const el = rootRef.current; if (!el) return;
    if (document.fullscreenElement) void document.exitFullscreen(); else void el.requestFullscreen?.();
  };

  const steps = procedure?.steps ?? [];

  return (
    <div
      ref={rootRef}
      style={{
        background: 'var(--bg)',
        minHeight: '100%',
        padding: 16,
        // Full-bleed: this is a fullscreen bench app on a desktop/tablet, so it
        // breaks out of the portal's centred content column to the whole width.
        width: '100vw',
        maxWidth: '100vw',
        marginLeft: 'calc(50% - 50vw)',
        marginRight: 'calc(50% - 50vw)',
        boxSizing: 'border-box',
      }}
    >
      <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
        <h1 className="view-title" style={{ marginBottom: 0 }}>Jakatori — operator · {ws?.name ?? 'workstation'}</h1>
        <div className="row" style={{ gap: 8 }}>
          <button className="btn sm" onClick={fullscreen}>⛶ Full screen</button>
          <button className="btn sm" onClick={() => (onExit ? onExit() : nav('/procguard'))}>Exit</button>
        </div>
      </div>

      {/* Video dominates; the staff/controls panel is a fixed side column. Both
          have a min basis so they stack on a narrow screen. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginTop: 12, alignItems: 'stretch' }}>
        {/* Staff / assessment header */}
        <div className="panel" style={{ flex: '1 1 300px', maxWidth: 380 }}>
          <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div><label>Operator name</label><input value={staffName} onChange={(e) => setStaffName(e.target.value)} placeholder="Bala" /></div>
            <div><label>Operator ID</label><input value={staffId} onChange={(e) => setStaffId(e.target.value)} placeholder="10018" /></div>
            <div><label>Assessment name</label><input value={assessName} onChange={(e) => setAssessName(e.target.value)} /></div>
            <div><label>Assessment number</label><input value={assessNo} onChange={(e) => setAssessNo(e.target.value)} /></div>
          </div>
          <div className="row" style={{ gap: 10, marginTop: 16, alignItems: 'center' }}>
            <button className={`btn ${running ? '' : 'primary'}`} onClick={() => setRunning((r) => !r)}>{running ? '■ Stop' : '▶ Start'}</button>
            <button className="btn" onClick={() => void doReset()}>↺ Reset</button>
            <span className={`badge ${blocked ? 'bad' : cycle ? 'good' : ''}`} style={{ fontSize: 14, marginLeft: 'auto' }}>
              {blocked ? 'BLOCKED' : cycle ? 'RUNNING' : 'READY'}
            </span>
            <span className="muted" style={{ fontSize: 13 }}>Cycle: <b>{cycleElapsed}s</b></span>
          </div>
          {blocked && (
            <div className="row" style={{ marginTop: 12, gap: 10, alignItems: 'center' }}>
              <span style={{ color: 'var(--bad)', fontSize: 13 }}>{blockedDev?.kind.replace(/_/g, ' ')} — {blockedDev?.detail}</span>
              <button className="btn sm primary" onClick={() => void doOverride()}>Override</button>
            </div>
          )}
        </div>

        {/* Live labelled video — grows to fill the remaining full-bleed width. */}
        <div className="panel" style={{ flex: '1000 1 480px', padding: 0, overflow: 'hidden' }}>
          <div style={{ position: 'relative', aspectRatio: '16 / 9', maxHeight: '78vh', margin: '0 auto', background: '#000' }}>
            {primaryCam != null && (
              <img src={`${snapshotBase}/cameras/${primaryCam}/snapshot?ts=${tick}`} alt="live" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = 'hidden'; }} />
            )}
            <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
              {/* Named ROIs with a live count (like DPC4=1) */}
              {steps.flatMap((s) => s.rois.map((r, ri) => {
                const cnt = dets.filter((d) => { const c = centre(d); return r.polygon.length >= 3 && pointInPoly(c.x, c.y, r.polygon); }).length;
                const cx = r.polygon.reduce((a, p) => a + p.x, 0) / r.polygon.length;
                const cy = r.polygon.reduce((a, p) => a + p.y, 0) / r.polygon.length;
                return (
                  <g key={`${s.seq}-${ri}`}>
                    <polygon points={r.polygon.map((p) => `${p.x * 100},${p.y * 100}`).join(' ')} fill="none" stroke="var(--warn)" strokeWidth={0.3} strokeDasharray="1 1" vectorEffect="non-scaling-stroke" />
                    <text x={cx * 100} y={cy * 100} fill="var(--warn)" fontSize={2.2} textAnchor="middle" style={{ fontWeight: 700 }}>{(r.name || `s${s.seq}`)}={cnt}</text>
                  </g>
                );
              }))}
              {/* Detection boxes */}
              {dets.map((d, i) => {
                const isPart = steps.some((s) => s.components.some((c) => c.partNumber.toLowerCase() === d.cls.toLowerCase()));
                const col = d.cls === 'operator' ? 'var(--teal)' : isPart ? 'var(--good)' : 'var(--accent)';
                return (
                  <g key={`d${i}`}>
                    <rect x={d.x * 100} y={d.y * 100} width={d.w * 100} height={d.h * 100} fill="none" stroke={col} strokeWidth={0.5} vectorEffect="non-scaling-stroke" />
                    <text x={d.x * 100 + 0.4} y={d.y * 100 - 0.6} fill={col} fontSize={2.4} style={{ fontWeight: 700 }}>{d.cls}</text>
                  </g>
                );
              })}
            </svg>
            <div style={{ position: 'absolute', top: 8, left: 10, fontFamily: 'var(--mono)', fontSize: 11.5, color: '#fff', textShadow: '0 1px 3px #000', lineHeight: 1.4 }}>
              <div>Frame :- {tick}</div>
              <div>SOP :- {procedure?.name ?? '—'}</div>
              <div>Activity :- {activeStep ? activeStep.name : cycle ? 'running' : 'idle'}</div>
            </div>
            {!running && <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 20, fontWeight: 800 }}>Stopped — press Start</div>}
          </div>
        </div>
      </div>

      {/* Assessment table */}
      <div className="panel" style={{ marginTop: 16 }}>
        {steps.length === 0 ? (
          <div className="muted">No published procedure for this workstation.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontVariantNumeric: 'tabular-nums' }}>
              <thead>
                <tr>
                  <th>Step</th><th>Activity</th><th>Std time</th><th>Acc time</th><th>Time taken</th>
                  <th>Range</th><th>Std count</th><th>Count by user</th><th>Evaluation</th><th>Remark</th>
                </tr>
              </thead>
              <tbody>
                {steps.map((s) => {
                  const taken = timeTakenS(s.seq);
                  const stdC = standardCount(s);
                  const actC = actualCount(s);
                  const a = assessStep({ standardTimeS: s.standardTimeS, acceptableTimeS: s.acceptableTimeS, timeTakenS: taken, standardCount: stdC, actualCount: actC });
                  return (
                    <tr key={s.seq}>
                      <td className="muted">{s.seq}</td>
                      <td>
                        {s.name}
                        {s.verifyBy === 'label' && (
                          <span className="class-chip neutral" style={{ marginLeft: 6, fontSize: 11 }} title="Verified by reading the component label / barcode">▦ label</span>
                        )}
                        {s.verifyBy === 'component' && (
                          <span className="class-chip neutral" style={{ marginLeft: 6, fontSize: 11 }} title="Verified by a trained component model">◈ model</span>
                        )}
                      </td>
                      <td>{s.standardTimeS ?? '—'}</td>
                      <td>{s.acceptableTimeS ?? '—'}</td>
                      <td>{taken != null ? `${taken.toFixed(0)}s` : '—'}</td>
                      <td style={{ color: a.range ? RANGE_TONE[a.range] : 'var(--text-dim)' }}>{a.range ? RANGE_LABEL[a.range] : '—'}</td>
                      <td>{stdC ?? '—'}</td>
                      <td style={{ color: stdC != null && actC !== stdC ? 'var(--warn)' : undefined }}>{actC ?? '—'}</td>
                      <td style={{ fontWeight: 700, color: a.evaluation === 'pass' ? 'var(--good)' : a.evaluation === 'fail' ? 'var(--bad)' : 'var(--text-dim)' }}>
                        {a.evaluation === 'pass' ? 'Pass' : a.evaluation === 'fail' ? 'Fail' : '—'}
                      </td>
                      <td className="muted" style={{ fontSize: 13 }}>{a.remark}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
